import * as React from 'react'
import { Suspense } from 'react'

import { ConnectError } from '@connectrpc/connect'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'

import { useAuth } from '@enechain/auth-js'
import {
  Loading,
  PageError,
  SideMenuLayout,
  SideMenuLayoutSecondaryMenuProvider,
} from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationRegular } from '@enechain/ecloud-icons'
import { ProductLogo, ProtectedAuthGuard } from '@enechain/eposition-ui'

import { MainLayoutBottomMenu } from '~/components/layouts/MainLayout/bottomMenu'
import { MainLayoutTopMenu } from '~/components/layouts/MainLayout/topMenu'
import { useTrackClickEvent } from '~/hooks/useTrackClickEvent'
import { useTrackUserAttribute } from '~/hooks/useTrackUserAttribute'

// memo(shunya): Design System 側の I/F の都合、修正次第削除
const DEFAULT_PAGE_ERROR_PROPERTIES = {
  Icon: FaTriangleExclamationRegular,
  title: '',
  description: '',
  buttonText: '',
}

// SideMenuLayout の logo/topMenu/bottomMenu にレンダー毎の inline 関数を渡すと
// 別コンポーネント扱いで再マウントされるため、モジュールスコープの安定参照にする
const LayoutLogo = (): React.ReactElement => <ProductLogo />
const LayoutTopMenu = (): React.ReactElement => <MainLayoutTopMenu />
const LayoutBottomMenu = (): React.ReactElement => (
  <MainLayoutBottomMenu eCloudUrl={import.meta.env.VITE_ECLOUD_URL} />
)

const MainLayout: React.FC = () => {
  const { t } = useTranslation(['component'])
  const { reset } = useQueryErrorResetBoundary()
  const { logout } = useAuth()
  useTrackUserAttribute()
  useTrackClickEvent()

  return (
    <ErrorBoundary
      FallbackComponent={({ ...rest }): React.ReactElement => {
        const error = rest.error as ConnectError
        return (
          <PageError
            kind="500"
            defaultErrorProperties={{
              '401': DEFAULT_PAGE_ERROR_PROPERTIES,
              '403': DEFAULT_PAGE_ERROR_PROPERTIES,
              '404': DEFAULT_PAGE_ERROR_PROPERTIES,
              '500': {
                Icon: FaTriangleExclamationRegular,
                title: t('component:layouts.main_layout.not_access'),
                description: `${t('component:layouts.main_layout.not_access_description')}\n${error.message}`,
                buttonText: t('component:layouts.main_layout.sign_out'),
              },
              '501': DEFAULT_PAGE_ERROR_PROPERTIES,
            }}
            onNavigate={(): void => logout()}
          />
        )
      }}
      onReset={reset}
    >
      <SideMenuLayoutSecondaryMenuProvider secondaryMenu={{}}>
        <SideMenuLayout
          bottomMenu={LayoutBottomMenu}
          defaultMenuLock={false}
          logo={LayoutLogo}
          topMenu={LayoutTopMenu}
        >
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </SideMenuLayout>
      </SideMenuLayoutSecondaryMenuProvider>
    </ErrorBoundary>
  )
}

export default ProtectedAuthGuard(MainLayout)
